// localStorage-backed daily submission counter for the PDP audit form.
// Friction, not security — the real gate is the server-side IP rate limit.
// Scoped by URL hash so re-submitting the exact same URL doesn't burn an
// attempt (a typo on the same URL counts once).

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "pdp_audit_submissions";
const DEFAULT_DAILY_LIMIT = 3;

interface SubmissionRecord {
    ts: number;
    urlHash: string;
}

function djb2(str: string): string {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
}

function readStorage(): SubmissionRecord[] {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeStorage(records: SubmissionRecord[]): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
        // Storage may be unavailable (private mode, quota); fail open.
    }
}

function startOfTodayMs(): number {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function todaysRecords(records: SubmissionRecord[]): SubmissionRecord[] {
    const cutoff = startOfTodayMs();
    return records.filter((r) => r.ts >= cutoff);
}

export interface UseAuditSubmissionLimitResult {
    used: number;
    remaining: number;
    isBlocked: boolean;
    bypassed: boolean;
    recordSubmission(url: string): void;
}

export interface UseAuditSubmissionLimitOptions {
    enabled?: boolean;
    dailyLimit?: number;
}

const noop = () => {};

export function useAuditSubmissionLimit(
    options: UseAuditSubmissionLimitOptions = {},
): UseAuditSubmissionLimitResult {
    const { enabled = true, dailyLimit = DEFAULT_DAILY_LIMIT } = options;
    const [used, setUsed] = useState(0);

    useEffect(() => {
        if (!enabled) return;
        const distinctUrls = new Set(
            todaysRecords(readStorage()).map((r) => r.urlHash),
        );
        setUsed(distinctUrls.size);
    }, [enabled]);

    const recordSubmission = useCallback(
        (url: string) => {
            if (!enabled) return;
            const all = readStorage();
            const today = todaysRecords(all);
            const urlHash = djb2(url);
            const alreadyToday = today.some((r) => r.urlHash === urlHash);
            const next: SubmissionRecord[] = [
                ...today,
                { ts: Date.now(), urlHash },
            ];
            writeStorage(next);
            if (!alreadyToday) {
                setUsed((u) => u + 1);
            }
        },
        [enabled],
    );

    if (!enabled) {
        return {
            used: 0,
            remaining: Infinity,
            isBlocked: false,
            bypassed: true,
            recordSubmission: noop,
        };
    }

    const remaining = Math.max(0, dailyLimit - used);
    return {
        used,
        remaining,
        isBlocked: remaining <= 0,
        bypassed: false,
        recordSubmission,
    };
}
